import { popperSameWidthModifier } from "@circle-react-uikit/Dropdown/utils";
import { useDebouncedValue } from "@circle-react/hooks/utils/useDebouncedValue";
import type { Topic } from "@circle-react/types/Topic";
import type { RefObject } from "react";
import { useEffect, useMemo, useRef } from "react";
import { usePopper } from "react-popper";
import { keepPreviousData } from "react-query";

import { useTopics } from "./useTopics";

interface UseTopicsDropdownProps {
  canCreateTopics: boolean;
  canSelectTopic: (topic: Topic) => boolean;
  hasMaxTopicsSelected: boolean;
  searchInputRef: RefObject<HTMLInputElement | null>;
  searchQuery: string;
  selectedTopics: Topic[];
  spaceId?: string;
}

export const useTopicsDropdown = ({
  spaceId,
  searchQuery,
  selectedTopics,
  hasMaxTopicsSelected,
  canSelectTopic,
  canCreateTopics,
  searchInputRef,
}: UseTopicsDropdownProps) => {
  const popperRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 200);

  const { data: topicsData, isPending } = useTopics({
    spaceId,
    query: debouncedSearchQuery,
    queryOptions: { placeholderData: keepPreviousData },
  });

  const dropdownTopics = useMemo(() => {
    const topics = topicsData?.records ?? [];
    const selectedTopicIds = selectedTopics.map(topic => topic.id);

    return topics.filter(
      topic => canSelectTopic(topic) && !selectedTopicIds.includes(topic.id),
    );
  }, [topicsData, selectedTopics, canSelectTopic]);

  const { styles, attributes, forceUpdate } = usePopper(
    searchInputRef.current,
    popperRef.current,
    {
      placement: "top-start",
      strategy: "absolute",
      modifiers: [
        popperSameWidthModifier,
        {
          name: "offset",
          options: { offset: [0, 2] },
        },
      ],
    },
  );

  // Update dropdown position when selected topics change
  useEffect(() => {
    forceUpdate?.();
  }, [forceUpdate, selectedTopics]);

  const shouldShowCreateTopic = useMemo(() => {
    if (!canCreateTopics || hasMaxTopicsSelected || searchQuery.length === 0) {
      return false;
    }

    const existingTopicNames = [
      ...selectedTopics.map(t => t.name.toLowerCase()),
      ...dropdownTopics.map(t => t.name.toLowerCase()),
    ];

    return !existingTopicNames.includes(searchQuery.toLowerCase());
  }, [
    canCreateTopics,
    hasMaxTopicsSelected,
    searchQuery,
    selectedTopics,
    dropdownTopics,
  ]);

  const shouldShowDropdown =
    !hasMaxTopicsSelected &&
    (isPending || shouldShowCreateTopic || dropdownTopics.length > 0);

  return {
    isPending,
    dropdownTopics,
    shouldShowCreateTopic,
    shouldShowDropdown,
    styles,
    attributes,
    popperRef,
    buttonRef,
  };
};
